import { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { Calculator as CalcIcon, Layers, Package, ChevronsRight } from "lucide-react";

const FENCE_TYPES = [
  { value: "wood", label: "Wood (Pressure Treated)" },
  { value: "cedar", label: "Cedar" },
  { value: "chain_link", label: "Chain Link" },
  { value: "ornamental_iron", label: "Ornamental Iron" },
  { value: "vinyl", label: "Vinyl" },
];

export default function CalculatorPage() {
  const [form, setForm] = useState({ fence_length: 100, fence_height: 6, fence_type: "wood", post_spacing: 8 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCalculate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/calculator/materials", form);
      setResult(data);
    } catch { toast.error("Calculation failed"); }
    finally { setLoading(false); }
  };

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="calculator-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 tracking-tight">Material Calculator</h1>
        <p className="text-sm text-zinc-500 mt-1">Estimate materials needed for your fence project</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Input Form */}
        <Card className="border border-zinc-200 shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-bold flex items-center gap-2">
              <CalcIcon className="w-5 h-5 text-orange-600" /> Project Specs
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Fence Type</Label>
              <Select value={form.fence_type} onValueChange={(v) => updateForm("fence_type", v)}>
                <SelectTrigger data-testid="calc-fence-type"><SelectValue /></SelectTrigger>
                <SelectContent>{FENCE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Fence Length (feet)</Label>
              <Input type="number" value={form.fence_length} onChange={(e) => updateForm("fence_length", parseFloat(e.target.value) || 0)} data-testid="calc-length" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Fence Height (feet)</Label>
              <Input type="number" value={form.fence_height} onChange={(e) => updateForm("fence_height", parseFloat(e.target.value) || 0)} data-testid="calc-height" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Post Spacing (feet)</Label>
              <Input type="number" value={form.post_spacing} onChange={(e) => updateForm("post_spacing", parseFloat(e.target.value) || 8)} data-testid="calc-spacing" />
            </div>
            <Button onClick={handleCalculate} disabled={loading} className="w-full bg-orange-600 hover:bg-orange-700 text-white field-btn" data-testid="calculate-btn">
              <ChevronsRight className="w-4 h-4 mr-2" /> {loading ? "Calculating..." : "Calculate Materials"}
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        <Card className={`border shadow-none transition-all ${result ? "border-orange-300 bg-orange-50/20" : "border-zinc-200"}`}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-bold flex items-center gap-2">
              <Package className="w-5 h-5 text-orange-600" /> Materials Needed
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!result ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Layers className="w-10 h-10 text-zinc-300 mb-3" />
                <p className="text-sm text-zinc-400">Enter specs and calculate</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="calc-results">
                <div className="bg-white border border-zinc-200 rounded-lg p-3 space-y-2.5">
                  {[
                    { label: "Posts Required", value: result.num_posts, unit: "posts" },
                    { label: "Concrete Bags", value: result.concrete_bags, unit: "bags" },
                    { label: "Rails", value: result.total_rails, unit: "rails" },
                    ...(result.boards_or_pickets > 0 ? [{ label: "Boards/Pickets", value: result.boards_or_pickets, unit: "boards" }] : []),
                    ...(result.panels > 0 ? [{ label: "Panels", value: result.panels, unit: "panels" }] : []),
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between py-1.5">
                      <span className="text-sm text-zinc-600">{item.label}</span>
                      <span className="text-sm font-bold text-zinc-900">{item.value} <span className="text-xs text-zinc-400 font-normal">{item.unit}</span></span>
                    </div>
                  ))}
                </div>
                <Separator />
                <div className="flex items-center justify-between py-2">
                  <span className="text-base font-bold text-zinc-900">Est. Material Cost</span>
                  <span className="text-xl font-extrabold text-orange-600" data-testid="calc-cost">
                    ${result.estimated_material_cost?.toLocaleString()}
                  </span>
                </div>
                <p className="text-xs text-zinc-400">
                  Based on {result.fence_length}ft of {result.fence_type?.replace(/_/g, " ")} fence at {result.fence_height}ft height.
                  Prices reflect Calgary, AB 2026 material costs (CAD).
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
